/**
 * Evidence-based training guidance content generator
 * Implements confidence gating (A-D) and phase-specific guidance
 * UK English, plain language, legally compliant
 */

// Evidence references for PDF export
export const evidenceReferences = [
  {
    title: 'WHO Guidelines on physical activity and sedentary behaviour',
    source: 'World Health Organization',
    year: 2020,
    link: 'https://www.who.int/publications/i/item/9789240015128'
  },
  {
    title: 'UK Chief Medical Officers Physical Activity Guidelines',
    source: 'Department of Health and Social Care',
    year: 2019,
    link: 'https://www.gov.uk/government/publications/physical-activity-guidelines-uk-chief-medical-officers-report'
  },
  {
    title: 'Youth Resistance Training: Updated Position Statement',
    source: 'Faigenbaum et al., NSCA',
    year: 2009,
    link: 'https://doi.org/10.1519/JSC.0b013e31819df407'
  },
  {
    title: 'Integrative Neuromuscular Training in Youth',
    source: 'Lloyd et al., British Journal of Sports Medicine',
    year: 2014,
    link: 'https://doi.org/10.1136/bjsports-2014-093462'
  },
  {
    title: 'Long-Term Athletic Development Position Statement',
    source: 'Lloyd et al., NSCA',
    year: 2016,
    link: 'https://doi.org/10.1519/SSC.0000000000000180'
  },
  {
    title: 'IOC Consensus Statement on Youth Athletic Development',
    source: 'Bergeron et al., British Journal of Sports Medicine',
    year: 2015,
    link: 'https://doi.org/10.1136/bjsports-2015-094962'
  }
];

// Safety caveats (embed verbatim)
export const safetyCaveats = [
  'This guidance is general and educational. It is not a personalised training plan and it is not medical advice.',
  'Children develop at different rates. Use confidence grade and your own context, coaching, and supervision to adapt.',
  'If pain persists for more than 14 days, or you are concerned about health or growth, contact a qualified professional.'
];

// Get stage meaning based on phase and confidence
export const getStageMeaning = (phase, confidenceGrade) => {
  const meanings = {
    pre_phv: {
      A: [
        'Your child is in the pre-rapid growth phase, typically characterised by steady, predictable growth.',
        'Coordination and movement skills develop well during this stage.',
        'This is an excellent time to build a broad foundation of movement patterns and basic strength.',
        'Growth velocity is moderate and relatively stable.',
        'The body adapts well to progressive training loads when introduced gradually.'
      ],
      B: [
        'Your child appears to be in the pre-rapid growth phase, with steady growth patterns.',
        'This stage typically supports good coordination development and movement skill acquisition.',
        'It is likely a good time to focus on movement variety and basic strength foundations.',
        'Growth velocity appears moderate, though more measurements would increase certainty.'
      ],
      C: [
        'Based on limited data, your child may be in a pre-rapid growth phase.',
        'This stage is often characterised by steady growth and good coordination.',
        'General age-appropriate activity guidance applies, with caution about phase-specific recommendations.',
        'More frequent measurements would improve confidence in this classification.'
      ],
      D: [
        'Insufficient measurement data to confidently determine maturation phase.',
        'General age-appropriate physical activity guidance is provided below.',
        'Regular measurements over time will enable more specific phase-based guidance.',
        'Focus on measurement consistency and technique for now.'
      ]
    },
    around_phv: {
      A: [
        'Your child is around their peak height velocity (PHV) – the period of most rapid growth.',
        'During this phase, coordination may temporarily feel less smooth as limb lengths change rapidly.',
        'Strength gains may plateau or slow as the body prioritises growth.',
        'This is a critical time for load management and technique focus.',
        'Recovery needs may increase; sleep and nutrition become especially important.'
      ],
      B: [
        'Your child appears to be approaching or around their peak height velocity.',
        'This phase often involves rapid growth and temporary coordination challenges.',
        'Load management and technique maintenance become priorities.',
        'More measurements would increase confidence in timing and guidance.'
      ],
      C: [
        'Limited data suggests your child may be in a rapid growth phase.',
        'If true, this period requires careful load management and technique focus.',
        'General guidance for adolescents in growth spurts is provided with caution.',
        'Increase measurement frequency to improve phase classification confidence.'
      ],
      D: [
        'Insufficient data to determine if rapid growth is occurring.',
        'General age-appropriate guidance is provided below.',
        'If you observe rapid height changes, prioritise measurement consistency.',
        'More data will enable phase-specific load management advice.'
      ]
    },
    post_phv_early: {
      A: [
        'Your child has passed peak height velocity and is in the early post-PHV phase.',
        'Growth rate is slowing, and coordination typically begins to stabilise.',
        'This is an excellent time to progressively rebuild strength and power.',
        'The body adapts well to increased training loads when progressed sensibly.',
        'Movement quality and technique should remain priorities alongside progressive overload.'
      ],
      B: [
        'Your child appears to be in the early post-PHV phase, with slowing growth.',
        'Coordination likely stabilises during this period, supporting progressive training.',
        'Strength and power development can be gradually increased.',
        'More measurements would confirm the phase transition timing.'
      ],
      C: [
        'Limited data suggests your child may have passed peak growth velocity.',
        'If true, this phase supports gradual increases in training load.',
        'General guidance for post-growth adolescents is provided with caution.',
        'Additional measurements will improve confidence in phase classification.'
      ],
      D: [
        'Insufficient data to determine maturation phase.',
        'General age-appropriate guidance is provided below.',
        'Regular measurements will enable phase-specific training recommendations.',
        'Focus on consistent measurement technique and frequency.'
      ]
    },
    post_phv_late: {
      A: [
        'Your child is in the late post-PHV phase, approaching adult stature.',
        'Growth has largely completed, and physical capabilities stabilise.',
        'This phase supports progressive strength, power, and sport-specific conditioning.',
        'Training can increasingly resemble adult programming, with appropriate supervision.',
        'Individual variation in maturity timing means continued monitoring is valuable.'
      ],
      B: [
        'Your child appears to be in the late post-PHV phase, nearing adult height.',
        'This stage typically supports progressive training and sport-specific development.',
        'Strength and conditioning can be advanced with proper technique and supervision.',
        'More measurements would confirm the completion of major growth.'
      ],
      C: [
        'Limited data suggests your child may be in a late maturation phase.',
        'General guidance for older adolescents is provided with caution.',
        'If growth has largely completed, progressive training is appropriate.',
        'Additional measurements will improve phase classification confidence.'
      ],
      D: [
        'Insufficient data to determine maturation phase.',
        'General age-appropriate guidance for adolescents is provided below.',
        'Regular measurements enable more specific training recommendations.',
        'Focus on measurement consistency for future insights.'
      ]
    },
    unknown: {
      A: ['Maturation phase could not be determined from available data.', 'General age-appropriate guidance is provided below.', 'Continue regular measurements to enable phase classification.'],
      B: ['Maturation phase could not be determined from available data.', 'General age-appropriate guidance is provided below.', 'Continue regular measurements to enable phase classification.'],
      C: ['Maturation phase could not be determined from available data.', 'General age-appropriate guidance is provided below.', 'Continue regular measurements to enable phase classification.'],
      D: ['Insufficient measurement data for maturation analysis.', 'General age-appropriate physical activity guidance is provided below.', 'Establish regular measurement routine (every 2-4 weeks recommended).']
    }
  };

  return meanings?.[phase]?.[confidenceGrade] || meanings?.unknown?.D;
};

// Training priorities (4 domains)
export const getTrainingPriorities = (phase, confidenceGrade, trainingExperience = 'some_experience') => {
  // Grade D: Only general guidance, no phase-specific
  if (confidenceGrade === 'D') {
    return [
      {
        domain: 'Movement skill and technique',
        focus: 'Broad movement literacy',
        why: 'Building a wide foundation of movement patterns supports long-term development and reduces injury risk.',
        whatItLooksLike: 'Running, jumping, landing, throwing, catching, balancing, crawling, climbing. Play-based and varied activities. Focus on quality, not quantity.'
      },
      {
        domain: 'Strength training and muscular fitness',
        focus: 'Bodyweight and basic resistance',
        why: 'Developing basic strength supports bone health, movement quality, and prepares the body for future training.',
        whatItLooksLike: 'Bodyweight squats, press-ups, planks, lunges. 2-3 sessions per week, 6-8 exercises, 1-2 sets, focus on technique. Supervision recommended.'
      },
      {
        domain: 'Speed, plyometrics, and change of direction',
        focus: 'Exposure and skill development',
        why: 'Regular exposure to sprinting and jumping builds neuromuscular competence and prepares tissues for sport demands.',
        whatItLooksLike: 'Short sprints (10-30m), skipping, hopping, low-height jumps. Quality over quantity. Stop before technique degrades.'
      },
      {
        domain: 'Aerobic development and general physical activity',
        focus: 'Daily activity and variety',
        why: 'Regular moderate to vigorous activity supports cardiovascular health, bone development, and overall wellbeing.',
        whatItLooksLike: 'Average 60 minutes per day of varied activity. Mix of play, sport, active travel, and structured sessions. Include rest days.'
      }
    ];
  }

  // Grade C: Possible phase guidance with wider caution
  if (confidenceGrade === 'C') {
    const basePriorities = getTrainingPriorities(phase, 'B', trainingExperience);
    return basePriorities?.map(p => ({
      ...p,
      whatItLooksLike: p?.whatItLooksLike + ' (Caution: phase classification has lower confidence – adjust based on observed growth and coordination.)'
    }));
  }

  // Grades A and B: Full phase-specific guidance
  const priorities = {
    pre_phv: [
      {
        domain: 'Movement skill and technique',
        focus: 'Movement literacy and variety',
        why: 'This phase offers excellent coordination for learning diverse movement patterns. A broad foundation supports long-term athletic development.',
        whatItLooksLike: 'Multi-directional running, jumping, landing, throwing, catching, balancing, agility drills. Sport sampling encouraged. Emphasise quality and fun.'
      },
      {
        domain: 'Strength training and muscular fitness',
        focus: 'Technique mastery and progressive loading',
        why: 'Building strength foundations during stable growth supports bone health, movement quality, and prepares for future demands.',
        whatItLooksLike: trainingExperience === 'new' ?'Bodyweight exercises: squats, press-ups, planks, lunges. 2 sessions per week, 6 exercises, 1-2 sets of 8-12 reps. Focus on form.'
          : trainingExperience === 'well_trained' ?'Bodyweight and light external loads: goblet squats, dumbbell presses, rows, lunges. 2-3 sessions per week, 6-8 exercises, 2-3 sets of 6-12 reps. Progress load when technique is consistent.' :'Bodyweight and light resistance: squats, press-ups, rows, lunges. 2-3 sessions per week, 6-8 exercises, 2 sets of 8-12 reps. Gradual progression.'
      },
      {
        domain: 'Speed, plyometrics, and change of direction',
        focus: 'Skill development and exposure',
        why: 'Regular exposure to high-velocity movements builds neuromuscular competence and tissue resilience.',
        whatItLooksLike: 'Short sprints (10-30m), acceleration drills, skipping, hopping, low to moderate plyometrics (box jumps, bounds). 1-2 sessions per week, low volume, high quality.'
      },
      {
        domain: 'Aerobic development and general physical activity',
        focus: 'Varied activity and aerobic base',
        why: 'Cardiovascular fitness supports overall health and provides a foundation for sport-specific conditioning.',
        whatItLooksLike: 'Average 60 minutes per day of moderate to vigorous activity. Mix of play, sport, active travel, and structured aerobic sessions (20-40 min, comfortable pace). Include rest days.'
      }
    ],
    around_phv: [
      {
        domain: 'Movement skill and technique',
        focus: 'Technique maintenance and quality',
        why: 'Rapid limb length changes can temporarily disrupt coordination. Prioritising quality over quantity protects movement patterns.',
        whatItLooksLike: 'Shorter, more frequent skill sessions. Focus on fundamental patterns: running mechanics, landing technique, posture. Reduce complexity if coordination feels off.'
      },
      {
        domain: 'Strength training and muscular fitness',
        focus: 'Maintain strength, conservative progression',
        why: 'Strength gains may plateau during rapid growth. Maintaining current levels and technique is a success. Avoid aggressive load increases.',
        whatItLooksLike: trainingExperience === 'new' ?'Bodyweight exercises: squats, press-ups, planks, rows. 2 sessions per week, 6 exercises, 1-2 sets of 8-12 reps. Do not increase load; focus on control.'
          : trainingExperience === 'well_trained' ?'Maintain current loads or reduce slightly if technique degrades. 2-3 sessions per week, 6-8 exercises, 2 sets of 6-10 reps. Prioritise trunk and hip control.' :'Bodyweight and light resistance: squats, press-ups, rows, lunges. 2 sessions per week, 6-8 exercises, 2 sets of 8-12 reps. Maintain loads, do not progress aggressively.'
      },
      {
        domain: 'Speed, plyometrics, and change of direction',
        focus: 'Reduce volume, maintain exposure',
        why: 'High-impact plyometrics and repeated high-speed work may increase discomfort during rapid growth. Maintain skill with lower volume.',
        whatItLooksLike: 'Short sprints (10-20m), low-volume plyometrics (reduce by 30-50% from pre-PHV). Focus on landing quality. Stop if pain or soreness persists.'
      },
      {
        domain: 'Aerobic development and general physical activity',
        focus: 'Low-impact conditioning options',
        why: 'Aerobic fitness can be maintained with lower-impact activities, reducing cumulative load during rapid growth.',
        whatItLooksLike: 'Swimming, cycling, elliptical, or shorter running sessions (15-30 min). Maintain frequency but reduce high-impact volume. Include extra recovery days if needed.'
      }
    ],
    post_phv_early: [
      {
        domain: 'Movement skill and technique',
        focus: 'Rebuild coordination and refine technique',
        why: 'As growth slows, coordination stabilises. This is an excellent time to refine movement quality and sport-specific skills.',
        whatItLooksLike: 'Progressive skill complexity: multi-directional agility, sport-specific drills, technique refinement. Increase session duration and complexity gradually.'
      },
      {
        domain: 'Strength training and muscular fitness',
        focus: 'Progressive strength and power development',
        why: 'Post-PHV, the body adapts well to increased training loads. Strength and power can be progressively developed.',
        whatItLooksLike: trainingExperience === 'new' ?'Bodyweight and light external loads: goblet squats, dumbbell presses, rows. 2-3 sessions per week, 6-8 exercises, 2-3 sets of 8-12 reps. Progress load gradually.'
          : trainingExperience === 'well_trained' ?'Moderate to heavy resistance: squats, deadlifts, presses, rows, Olympic lift variations. 3 sessions per week, 6-8 exercises, 3-4 sets of 4-8 reps. Progressive overload with good form.' :'Bodyweight and moderate resistance: squats, presses, rows, lunges. 2-3 sessions per week, 6-8 exercises, 2-3 sets of 6-10 reps. Gradual load progression.'
      },
      {
        domain: 'Speed, plyometrics, and change of direction',
        focus: 'Progressive power development',
        why: 'Neuromuscular system adapts well post-PHV. Plyometric and speed volumes can be progressively increased.',
        whatItLooksLike: 'Moderate to high plyometrics: box jumps, bounds, depth jumps (if experienced). Sprint work: 20-60m, acceleration and max velocity. 1-2 sessions per week, progressive volume.'
      },
      {
        domain: 'Aerobic development and general physical activity',
        focus: 'Sport-specific conditioning build',
        why: 'Aerobic capacity can be progressively developed to support sport demands.',
        whatItLooksLike: 'Sport-specific conditioning: interval training, repeated sprints, game-based conditioning. 2-3 sessions per week, 20-45 min. Progress intensity and volume gradually.'
      }
    ],
    post_phv_late: [
      {
        domain: 'Movement skill and technique',
        focus: 'Sport-specific mastery and refinement',
        why: 'Physical maturity supports advanced skill development and sport-specific movement patterns.',
        whatItLooksLike: 'Advanced sport-specific drills, tactical training, high-complexity movement patterns. Maintain technique standards under fatigue.'
      },
      {
        domain: 'Strength training and muscular fitness',
        focus: 'Advanced strength and power programming',
        why: 'Training can increasingly resemble adult programming, with appropriate supervision and individualisation.',
        whatItLooksLike: trainingExperience === 'new' ?'Structured resistance training: squats, presses, rows, deadlifts. 2-3 sessions per week, 6-8 exercises, 3 sets of 6-10 reps. Supervised progression.'
          : trainingExperience === 'well_trained' ?'Periodised strength and power training: heavy resistance, Olympic lifts, plyometrics. 3-4 sessions per week, sport-specific programming. Professional coaching recommended.' :'Moderate to heavy resistance: squats, deadlifts, presses, rows. 3 sessions per week, 6-8 exercises, 3-4 sets of 5-8 reps. Progressive overload with supervision.'
      },
      {
        domain: 'Speed, plyometrics, and change of direction',
        focus: 'High-intensity power and speed development',
        why: 'Mature neuromuscular system supports advanced plyometric and speed training.',
        whatItLooksLike: 'Advanced plyometrics: depth jumps, reactive work, sport-specific power. Sprint training: max velocity, repeated sprints. 2 sessions per week, high intensity, managed volume.'
      },
      {
        domain: 'Aerobic development and general physical activity',
        focus: 'Sport-specific conditioning and periodisation',
        why: 'Conditioning can be tailored to sport demands and competitive calendar.',
        whatItLooksLike: 'Periodised conditioning: high-intensity intervals, sport-specific endurance, tactical conditioning. 2-3 sessions per week, integrated with strength and sport training.'
      }
    ]
  };

  return priorities?.[phase] || priorities?.pre_phv;
};

// Example week templates
export const getExampleWeekTemplates = (phase, confidenceGrade, trainingExperience = 'some_experience') => {
  if (confidenceGrade === 'D') {
    return {
      teamSport: [
        'Monday: Sport practice (60-90 min) – skills, small-sided games',
        'Tuesday: Strength fundamentals (30-40 min) – bodyweight exercises, technique focus',
        'Wednesday: Active recovery or rest – light play, walking, stretching',
        'Thursday: Sport practice (60-90 min) – tactical work, conditioning games',
        'Friday: Movement and mobility (20-30 min) – dynamic warm-up patterns, flexibility',
        'Saturday: Match or sport session (60-90 min)',
        'Sunday: Rest or light active recovery'
      ],
      endurance: [
        'Monday: Easy aerobic session (20-30 min) – comfortable pace, low impact option',
        'Tuesday: Strength fundamentals (30-40 min) – bodyweight exercises, core work',
        'Wednesday: Moderate aerobic session (25-35 min) – steady pace',
        'Thursday: Rest or light cross-training (20-30 min) – swimming, cycling',
        'Friday: Movement quality (20-30 min) – drills, mobility, technique',
        'Saturday: Longer aerobic session (35-50 min) – comfortable pace, varied terrain',
        'Sunday: Rest or very light activity'
      ]
    };
  }

  const templates = {
    pre_phv: {
      teamSport: trainingExperience === 'new'
        ? [
            'Monday: Sport practice (60-75 min) – skills, games, fun focus',
            'Tuesday: Strength fundamentals (25-35 min) – bodyweight, technique learning',
            'Wednesday: Active play or rest – unstructured activity encouraged',
            'Thursday: Sport practice (60-75 min) – skills, small-sided games',
            'Friday: Movement skills (20-30 min) – running, jumping, landing drills',
            'Saturday: Match or sport session (60-90 min)',
            'Sunday: Rest or family activity'
          ]
        : trainingExperience === 'well_trained'
        ? [
            'Monday: Sport practice (75-90 min) – technical and tactical work',
            'Tuesday: Strength session (40-50 min) – progressive resistance, 6-8 exercises',
            'Wednesday: Speed and agility (30-40 min) – short sprints, change of direction',
            'Thursday: Sport practice (75-90 min) – game-based conditioning',
            'Friday: Strength session (40-50 min) – different exercises from Tuesday',
            'Saturday: Match or competition (60-120 min)',
            'Sunday: Active recovery (20-30 min) – light movement, stretching, or rest'
          ]
        : [
            'Monday: Sport practice (60-90 min) – skills and game play',
            'Tuesday: Strength fundamentals (30-40 min) – bodyweight and light resistance',
            'Wednesday: Active recovery or rest – light play, walking',
            'Thursday: Sport practice (60-90 min) – conditioning and tactics',
            'Friday: Movement quality (25-35 min) – speed drills, plyometric exposure',
            'Saturday: Match or sport session (60-90 min)',
            'Sunday: Rest or light activity'
          ],
      endurance: trainingExperience === 'new'
        ? [
            'Monday: Easy run or activity (20-30 min) – comfortable pace',
            'Tuesday: Strength fundamentals (25-35 min) – bodyweight exercises',
            'Wednesday: Cross-training (25-35 min) – swimming, cycling, or rest',
            'Thursday: Moderate run (25-35 min) – steady effort',
            'Friday: Movement skills (20-25 min) – drills, mobility',
            'Saturday: Longer easy session (35-45 min) – comfortable pace',
            'Sunday: Rest or very light activity'
          ]
        : trainingExperience === 'well_trained'
        ? [
            'Monday: Easy aerobic (30-40 min) – recovery pace',
            'Tuesday: Strength session (40-50 min) – progressive resistance',
            'Wednesday: Moderate aerobic with strides (35-45 min) – include 4-6 short accelerations',
            'Thursday: Easy cross-training (30-40 min) – swimming or cycling',
            'Friday: Movement quality and speed (30-35 min) – drills, short sprints',
            'Saturday: Longer aerobic session (50-70 min) – comfortable pace, varied terrain',
            'Sunday: Rest or very light active recovery'
          ]
        : [
            'Monday: Easy aerobic (25-35 min) – comfortable pace',
            'Tuesday: Strength fundamentals (30-40 min) – bodyweight and light resistance',
            'Wednesday: Moderate aerobic (30-40 min) – steady effort',
            'Thursday: Rest or light cross-training (20-30 min)',
            'Friday: Movement quality (25-30 min) – drills, mobility, short sprints',
            'Saturday: Longer aerobic session (40-55 min) – comfortable pace',
            'Sunday: Rest or light activity'
          ]
    },
    around_phv: {
      teamSport: trainingExperience === 'new'
        ? [
            'Monday: Sport practice (60-75 min) – technique focus, reduced intensity',
            'Tuesday: Strength maintenance (25-30 min) – bodyweight, control focus',
            'Wednesday: Rest or very light activity – prioritise recovery',
            'Thursday: Sport practice (60-75 min) – skills, moderate intensity',
            'Friday: Movement quality (20-25 min) – landing drills, posture work',
            'Saturday: Match or sport session (60-90 min) – monitor for discomfort',
            'Sunday: Rest – sleep and recovery priority'
          ]
        : trainingExperience === 'well_trained'
        ? [
            'Monday: Sport practice (70-90 min) – technical work, manage high-impact volume',
            'Tuesday: Strength maintenance (35-45 min) – maintain loads, technique priority',
            'Wednesday: Low-impact conditioning (25-35 min) – swimming, cycling, or rest',
            'Thursday: Sport practice (70-90 min) – tactical work, reduced plyometric volume',
            'Friday: Movement quality (25-30 min) – landing mechanics, trunk control',
            'Saturday: Match or competition (60-120 min) – monitor for pain or discomfort',
            'Sunday: Active recovery or rest – prioritise sleep and nutrition'
          ]
        : [
            'Monday: Sport practice (60-90 min) – technique focus, manage intensity',
            'Tuesday: Strength maintenance (30-35 min) – maintain loads, do not progress',
            'Wednesday: Rest or light activity – recovery priority',
            'Thursday: Sport practice (60-90 min) – skills and moderate conditioning',
            'Friday: Movement quality (20-30 min) – landing drills, mobility',
            'Saturday: Match or sport session (60-90 min) – monitor for discomfort',
            'Sunday: Rest – sleep and recovery'
          ],
      endurance: trainingExperience === 'new'
        ? [
            'Monday: Easy low-impact (20-25 min) – swimming or cycling preferred',
            'Tuesday: Strength maintenance (25-30 min) – bodyweight, control focus',
            'Wednesday: Rest or very light activity',
            'Thursday: Easy low-impact (20-30 min) – comfortable pace',
            'Friday: Movement quality (15-20 min) – drills, mobility',
            'Saturday: Moderate low-impact (30-40 min) – swimming, cycling, or easy run',
            'Sunday: Rest – prioritise recovery'
          ]
        : trainingExperience === 'well_trained'
        ? [
            'Monday: Easy aerobic (25-35 min) – low-impact option preferred',
            'Tuesday: Strength maintenance (35-40 min) – maintain loads, technique priority',
            'Wednesday: Low-impact cross-training (30-40 min) – swimming or cycling',
            'Thursday: Easy aerobic (25-35 min) – comfortable pace, shorten if needed',
            'Friday: Movement quality (20-25 min) – drills, mobility, no high-impact',
            'Saturday: Moderate aerobic (40-50 min) – low-impact preferred, or shorter run',
            'Sunday: Rest or very light active recovery'
          ]
        : [
            'Monday: Easy aerobic (20-30 min) – low-impact option preferred',
            'Tuesday: Strength maintenance (30-35 min) – maintain loads, do not progress',
            'Wednesday: Rest or light cross-training (20-25 min)',
            'Thursday: Easy aerobic (25-35 min) – comfortable pace',
            'Friday: Movement quality (20-25 min) – drills, mobility',
            'Saturday: Moderate aerobic (35-45 min) – low-impact preferred',
            'Sunday: Rest'
          ]
    },
    post_phv_early: {
      teamSport: trainingExperience === 'new'
        ? [
            'Monday: Sport practice (60-90 min) – skills and game play',
            'Tuesday: Strength progression (30-40 min) – gradual load increases',
            'Wednesday: Active recovery or rest',
            'Thursday: Sport practice (60-90 min) – conditioning and tactics',
            'Friday: Speed and power (30-35 min) – progressive plyometric volume',
            'Saturday: Match or sport session (60-120 min)',
            'Sunday: Rest or light activity'
          ]
        : trainingExperience === 'well_trained'
        ? [
            'Monday: Sport practice (75-90 min) – technical and tactical work',
            'Tuesday: Strength session (45-55 min) – progressive resistance, 6-8 exercises',
            'Wednesday: Speed and power (35-45 min) – plyometrics, sprint work',
            'Thursday: Sport practice (75-90 min) – game-based conditioning',
            'Friday: Strength session (45-55 min) – different exercises from Tuesday',
            'Saturday: Match or competition (60-120 min)',
            'Sunday: Active recovery (25-35 min) – light movement, stretching, or rest'
          ]
        : [
            'Monday: Sport practice (60-90 min) – skills and game play',
            'Tuesday: Strength progression (35-45 min) – progressive resistance',
            'Wednesday: Active recovery or rest',
            'Thursday: Sport practice (60-90 min) – conditioning and tactics',
            'Friday: Speed and power (30-40 min) – plyometrics, sprint drills',
            'Saturday: Match or sport session (60-120 min)',
            'Sunday: Rest or light activity'
          ],
      endurance: trainingExperience === 'new'
        ? [
            'Monday: Easy aerobic (25-35 min) – comfortable pace',
            'Tuesday: Strength progression (30-40 min) – gradual load increases',
            'Wednesday: Moderate aerobic (30-40 min) – steady effort',
            'Thursday: Cross-training (25-35 min) – swimming, cycling, or rest',
            'Friday: Movement quality and speed (25-30 min) – drills, short sprints',
            'Saturday: Longer aerobic session (45-60 min) – comfortable pace',
            'Sunday: Rest or very light activity'
          ]
        : trainingExperience === 'well_trained'
        ? [
            'Monday: Easy aerobic (35-45 min) – recovery pace',
            'Tuesday: Strength session (45-55 min) – progressive resistance',
            'Wednesday: Moderate aerobic with intervals (40-50 min) – include 4-6 x 2-3 min efforts',
            'Thursday: Easy cross-training (35-45 min) – swimming or cycling',
            'Friday: Speed and power (30-40 min) – plyometrics, sprint work',
            'Saturday: Longer aerobic session (60-90 min) – comfortable pace, varied terrain',
            'Sunday: Rest or very light active recovery'
          ]
        : [
            'Monday: Easy aerobic (30-40 min) – comfortable pace',
            'Tuesday: Strength progression (35-45 min) – progressive resistance',
            'Wednesday: Moderate aerobic (35-45 min) – steady effort',
            'Thursday: Cross-training (30-40 min) – swimming, cycling, or rest',
            'Friday: Speed and power (30-35 min) – plyometrics, sprint drills',
            'Saturday: Longer aerobic session (50-70 min) – comfortable pace',
            'Sunday: Rest or light activity'
          ]
    },
    post_phv_late: {
      teamSport: trainingExperience === 'new'
        ? [
            'Monday: Sport practice (75-90 min) – technical and tactical work',
            'Tuesday: Strength training (35-45 min) – structured resistance program',
            'Wednesday: Speed and power (30-40 min) – plyometrics, sprint work',
            'Thursday: Sport practice (75-90 min) – game-based conditioning',
            'Friday: Strength training (35-45 min) – different exercises from Tuesday',
            'Saturday: Match or competition (60-120 min)',
            'Sunday: Active recovery or rest'
          ]
        : trainingExperience === 'well_trained'
        ? [
            'Monday: Sport practice (75-90 min) – technical and tactical work',
            'Tuesday: Strength and power (50-60 min) – heavy resistance, Olympic lift variations',
            'Wednesday: Speed and power (40-50 min) – advanced plyometrics, max velocity sprints',
            'Thursday: Sport practice (75-90 min) – high-intensity conditioning',
            'Friday: Strength and power (50-60 min) – different emphasis from Tuesday',
            'Saturday: Match or competition (60-120 min)',
            'Sunday: Active recovery (30-40 min) – light movement, stretching, or rest'
          ]
        : [
            'Monday: Sport practice (75-90 min) – technical and tactical work',
            'Tuesday: Strength training (40-50 min) – progressive resistance program',
            'Wednesday: Speed and power (35-45 min) – plyometrics, sprint work',
            'Thursday: Sport practice (75-90 min) – game-based conditioning',
            'Friday: Strength training (40-50 min) – different exercises from Tuesday',
            'Saturday: Match or competition (60-120 min)',
            'Sunday: Active recovery or rest'
          ],
      endurance: trainingExperience === 'new'
        ? [
            'Monday: Easy aerobic (30-40 min) – recovery pace',
            'Tuesday: Strength training (35-45 min) – structured resistance program',
            'Wednesday: Moderate aerobic with tempo (40-50 min) – include 15-20 min steady effort',
            'Thursday: Cross-training (30-40 min) – swimming, cycling, or rest',
            'Friday: Speed and power (30-35 min) – plyometrics, sprint drills',
            'Saturday: Longer aerobic session (60-80 min) – comfortable pace',
            'Sunday: Rest or very light activity'
          ]
        : trainingExperience === 'well_trained'
        ? [
            'Monday: Easy aerobic (40-50 min) – recovery pace',
            'Tuesday: Strength and power (50-60 min) – periodised resistance program',
            'Wednesday: Interval session (45-60 min) – sport-specific high-intensity work',
            'Thursday: Easy cross-training (40-50 min) – swimming or cycling',
            'Friday: Speed and power (35-45 min) – advanced plyometrics, sprint work',
            'Saturday: Long aerobic session (75-120 min) – comfortable pace, sport-specific terrain',
            'Sunday: Rest or very light active recovery'
          ]
        : [
            'Monday: Easy aerobic (35-45 min) – recovery pace',
            'Tuesday: Strength training (40-50 min) – progressive resistance program',
            'Wednesday: Moderate aerobic with intervals (45-55 min) – include 5-8 x 2-3 min efforts',
            'Thursday: Cross-training (35-45 min) – swimming, cycling, or rest',
            'Friday: Speed and power (30-40 min) – plyometrics, sprint work',
            'Saturday: Longer aerobic session (70-90 min) – comfortable pace',
            'Sunday: Rest or light activity'
          ]
    }
  };

  return {
    teamSport: templates?.[phase]?.teamSport || templates?.pre_phv?.teamSport,
    endurance: templates?.[phase]?.endurance || templates?.pre_phv?.endurance
  };
};

// Example sessions library (6 required types)
export const exampleSessions = [
  {
    id: 'strength_fundamentals',
    title: 'Strength Fundamentals – Full Body (Youth Friendly)',
    goal: 'Develop basic strength, movement quality, and neuromuscular control using bodyweight and light resistance.',
    warmUp: '5-10 minutes: Dynamic movements (arm circles, leg swings, lunges, squats, light jogging, skipping). Goal: Raise heart rate, mobilise joints, prepare for movement.',
    mainSet: [
      '1. Bodyweight squat: 2-3 sets of 8-12 reps (focus: depth, knee alignment, upright torso)',
      '2. Press-up (or incline press-up): 2-3 sets of 6-12 reps (focus: straight body line, controlled descent)',
      '3. Bodyweight row (or ring row): 2-3 sets of 8-12 reps (focus: shoulder blade squeeze, controlled pull)',
      '4. Forward lunge (alternating): 2-3 sets of 6-10 reps per leg (focus: knee tracking, balance)',
      '5. Plank hold: 2-3 sets of 20-40 seconds (focus: neutral spine, no sagging)',
      '6. Glute bridge: 2-3 sets of 10-15 reps (focus: hip extension, squeeze at top)',
      'Rest: 60-90 seconds between sets. Adjust based on technique and fatigue.'
    ],
    coolDown: '5-10 minutes: Light walking, static stretching (quads, hamstrings, hip flexors, chest, shoulders). Goal: Gradual heart rate reduction, promote recovery.',
    progression: 'Progress only when technique is consistent across all sets. Options: increase reps (up to 15), add a set, increase range of motion, add light external load (e.g., goblet squat with light dumbbell).',
    regression: 'If pain, excessive fatigue, or poor technique: reduce reps, reduce sets, increase rest, use easier variations (e.g., box squat, wall press-up, assisted row). If pain persists beyond 14 days, seek qualified assessment.'
  },
  {
    id: 'jump_landing',
    title: 'Jump and Landing Mechanics – Low Volume',
    goal: 'Develop safe landing technique, reactive strength, and neuromuscular control. Low volume, high quality.',
    warmUp: '8-12 minutes: Dynamic warm-up (jogging, skipping, high knees, butt kicks, leg swings, bodyweight squats, lunges). Include 3-5 submaximal jumps to prepare nervous system.',
    mainSet: [
      '1. Landing drill (drop from low box, 20-30 cm): 3 sets of 4-6 reps (focus: soft landing, knee alignment, balance, quiet landing)',
      '2. Countermovement jump: 3 sets of 4-6 reps (focus: arm swing, explosive extension, soft landing)',
      '3. Broad jump: 3 sets of 3-5 reps (focus: horizontal power, controlled landing, stick the landing)',
      '4. Single-leg hop (in place): 2 sets of 4-6 reps per leg (focus: balance, knee control, soft landing)',
      '5. Skipping for height: 2 sets of 10-15 m (focus: rhythm, knee drive, elastic rebound)',
      'Rest: 90-120 seconds between sets. Quality over quantity. Stop if technique degrades.'
    ],
    coolDown: '5-10 minutes: Light jogging or walking, static stretching (calves, quads, hamstrings, hip flexors). Goal: Gradual recovery, reduce muscle tension.',
    progression: 'Progress volume slowly (add 1-2 reps per set, or add 1 set). Increase box height for drops (max 40-50 cm for youth). Add reactive elements (e.g., drop jump with immediate rebound) only when landing quality is excellent.',
    regression: 'If pain, poor landing quality, or excessive fatigue: reduce volume by 50%, lower box height, remove reactive elements, focus on landing drills only. If pain persists, stop plyometrics and seek assessment.'
  },
  {
    id: 'sprint_mechanics',
    title: 'Sprint Mechanics and Accelerations – Short, High Quality',
    goal: 'Develop sprint technique, acceleration mechanics, and neuromuscular power. Short distances, long rest, quality first.',
    warmUp: '10-15 minutes: Dynamic warm-up (jogging, high knees, butt kicks, A-skips, B-skips, leg swings, lunges, bodyweight squats). Include 3-4 build-up runs (50-70% effort, 20-30 m) to prepare for sprinting.',
    mainSet: [
      '1. Acceleration drill (10 m): 4-6 reps (focus: forward lean, powerful push, arm drive, gradual upright transition)',
      '2. Short sprint (20 m): 4-6 reps (focus: max effort, technique maintenance, relaxed shoulders)',
      '3. Flying sprint (10 m fly zone after 10 m build-up): 3-5 reps (focus: max velocity mechanics, upright posture, arm-leg coordination)',
      '4. Change of direction (5 m out, 5 m back): 3-5 reps (focus: deceleration control, quick direction change, re-acceleration)',
      'Rest: 2-3 minutes between reps, 3-4 minutes between sets. Full recovery is essential for quality.'
    ],
    coolDown: '5-10 minutes: Light jogging or walking, static stretching (hip flexors, quads, hamstrings, calves, glutes). Goal: Gradual recovery, maintain flexibility.',
    progression: 'Progress distance gradually (up to 30-40 m for accelerations, 60 m for sprints). Add reps cautiously (max 8-10 per session). Introduce resisted sprints (light sled) or assisted sprints (downhill, slight grade) only with coaching supervision.',
    regression: 'If pain, technique breakdown, or excessive fatigue: reduce distance (back to 10 m), reduce reps (3-4 per drill), increase rest. Focus on drills (A-skips, wall drives) instead of full sprints. If pain persists, seek assessment.'
  },
  {
    id: 'aerobic_base',
    title: 'Aerobic Base Session – Low Impact Option Included',
    goal: 'Develop aerobic capacity and cardiovascular fitness using comfortable, sustainable effort. Low-impact options available.',
    warmUp: '5-10 minutes: Gradual increase in effort from very easy to comfortable pace. Dynamic movements (arm swings, leg swings, light skipping) if running.',
    mainSet: [
      'Option A (Running): 20-40 minutes continuous running at comfortable pace (able to hold conversation). Vary terrain if possible (grass, trails, slight hills).',
      'Option B (Low-impact): 25-45 minutes swimming, cycling, or elliptical at comfortable, steady effort. Focus: smooth rhythm, controlled breathing.',
      'Option C (Mixed): 15-20 minutes running + 15-20 minutes cycling or swimming. Transition smoothly between activities.',
      'Effort level: 6-7 out of 10 (moderate). Should feel sustainable for the entire duration.'
    ],
    coolDown: '5-10 minutes: Gradual reduction in effort to very easy pace. Light walking, static stretching (major muscle groups). Goal: Gradual recovery, promote circulation.',
    progression: 'Increase duration by 5-10 minutes per week (max 60-70 min for youth). Introduce gentle tempo efforts (10-15 min at slightly harder effort within the session) once base is established. Vary terrain and activities for engagement.',
    regression: 'If excessive fatigue, pain, or loss of enjoyment: reduce duration by 25-50%, switch to low-impact option, reduce frequency (e.g., 2 sessions per week instead of 3). If pain persists, seek assessment.'
  },
  {
    id: 'mobility_movement',
    title: 'Mobility and Movement Quality Session – Short',
    goal: 'Improve joint mobility, movement quality, and body awareness. Reduce injury risk and support recovery.',
    warmUp: '3-5 minutes: Light movement (walking, gentle jogging, arm circles). Goal: Raise body temperature slightly.',
    mainSet: [
      '1. Hip mobility circuit: 90/90 hip stretch, hip flexor lunge stretch, leg swings (front-back, side-side). 2 sets, 8-10 reps or 20-30 sec holds per movement.',
      '2. Thoracic spine mobility: Quadruped thoracic rotations, cat-cow stretches. 2 sets, 8-10 reps per movement.',
      '3. Ankle mobility: Ankle dorsiflexion stretch (wall lean), ankle circles. 2 sets, 10-15 reps or 20-30 sec holds per side.',
      '4. Shoulder mobility: Arm circles, shoulder dislocates (with band or stick), wall slides. 2 sets, 8-10 reps per movement.',
      '5. Movement quality drills: Bodyweight squat (slow, controlled), single-leg balance (20-30 sec per leg), walking lunge with rotation. 2 sets, 6-8 reps per movement.',
      'Total session: 20-30 minutes. Focus: controlled movement, no pain, gentle stretch sensation.'
    ],
    coolDown: '3-5 minutes: Light walking, deep breathing. Goal: Relaxation, mental recovery.',
    progression: 'Increase range of motion gradually. Add complexity (e.g., single-leg balance with eyes closed, dynamic stretches with movement). Increase holds to 40-60 seconds if comfortable.',
    regression: 'If pain or excessive stiffness: reduce range of motion, reduce holds to 10-15 seconds, skip movements that cause discomfort. Focus on pain-free movements only. If pain persists, seek assessment.'
  },
  {
    id: 'recovery_light',
    title: 'Recovery, Sleep, and "Light Day" Guidance',
    goal: 'Promote active recovery, support adaptation, and emphasise the importance of sleep and rest.',
    warmUp: 'Not applicable – this is a recovery-focused session.',
    mainSet: [
      'Option A (Active recovery): 15-25 minutes very light activity (easy walk, gentle swim, easy cycling, yoga, stretching). Effort: 3-4 out of 10 (very easy, relaxing).',
      'Option B (Complete rest): No structured activity. Focus on sleep, nutrition, hydration, and relaxation.',
      'Sleep guidance: Aim for 8-10 hours per night for adolescents. Consistent sleep schedule supports recovery, growth, and performance.',
      'Nutrition guidance: Balanced meals with protein, carbohydrates, and vegetables. Hydration throughout the day. No supplement advice provided.',
      'Mental recovery: Enjoyable activities, social time, hobbies outside sport. Balance is essential for long-term development.'
    ],
    coolDown: 'Not applicable – entire session is recovery-focused.',
    progression: 'No progression needed. Recovery sessions remain light. Adjust frequency based on training load (more recovery if training is intense).',
    regression: 'If feeling very fatigued or unwell: choose complete rest over active recovery. Prioritise sleep and nutrition. If fatigue persists beyond a few days, reduce training load and consider consultation with coach or healthcare provider.'
  }
];

// Load management rules (stage-specific)
export const getLoadManagementRules = (phase, confidenceGrade) => {
  const baseRules = [
    'Avoid sudden spikes in total training volume or intensity (increase by no more than 10-15% per week).',
    'Prioritise quality over quantity in all training sessions.',
    'If a new pain persists beyond 14 days, seek qualified assessment. Pain does not equal harm, but persistent pain needs attention.',
    'Monitor sleep, appetite, mood, and enjoyment. Persistent changes may indicate excessive load.',
    'Include at least one full rest day per week, more if needed.'
  ];

  if (confidenceGrade === 'D') {
    return [
      ...baseRules,
      'Without phase classification, use general age-appropriate load management principles.',
      'Establish regular measurement routine to enable phase-specific guidance in future.'
    ];
  }

  const phaseRules = {
    pre_phv: [
      'Keep high-impact work short and well-spaced (48-72 hours between plyometric or sprint sessions).',
      'Encourage multi-sport participation and varied movement patterns.',
      'Strength progression should be gradual; technique mastery before load increases.'
    ],
    around_phv: [
      'Strongly prioritise load management during rapid growth – this is the highest-risk period.',
      'Reduce high-impact plyometric volume by 30-50% compared to pre-PHV phase.',
      'Keep high-impact work short and well-spaced when growth velocity is high.',
      'Use low-impact conditioning options (swimming, cycling) to maintain fitness.',
      'Do not aggressively increase strength training loads; maintenance is success.',
      'Extra recovery days are appropriate if pain or soreness is present.'
    ],
    post_phv_early: [
      'Gradually re-progress training loads as coordination stabilises.',
      'Increase plyometric and sprint volumes progressively (10-15% per week).',
      'Strength training can be advanced, but maintain technique standards.',
      'Monitor for delayed effects of previous rapid growth (some discomfort may linger).'
    ],
    post_phv_late: [
      'Training can increasingly resemble adult programming, with appropriate supervision.',
      'Periodisation becomes important: vary intensity and volume across training cycles.',
      'High training loads are appropriate if built progressively and monitored carefully.',
      'Individual variation in recovery capacity requires personalised load management.'
    ]
  };

  const specificRules = phaseRules?.[phase] || phaseRules?.pre_phv;
  return [...baseRules, ...specificRules];
};

export default {
  evidenceReferences,
  safetyCaveats,
  getStageMeaning,
  getTrainingPriorities,
  getExampleWeekTemplates,
  exampleSessions,
  getLoadManagementRules
};